import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import LogoMark from "./LogoMark";
import { Instagram, Facebook, Youtube, Link as LinkIcon } from "lucide-react";
import { SOCIAL_LINKS } from "../lib/funding";

const FOOTER_LINK_GROUPS = [
  {
    title: "Network",
    links: [
      { label: "Interviews", href: "/episodes" },
      { label: "Editorial", href: "/articles" },
      { label: "Directory", href: "/directory" },
      { label: "The Vlog", href: "/vlog" },
      { label: "Subscribe", href: "/subscribe" },
    ],
  },
  {
    title: "Funding Portal",
    links: [
      { label: "Pricing", href: "/funding#pricing" },
      { label: "AI Report — $139", href: "/funding/apply?plan=ai_report" },
      { label: "WealthFirst PRO", href: "/funding/apply?plan=pro_monthly" },
      { label: "AutoPilot · 7-day trial", href: "/funding/apply?plan=autopilot" },
      { label: "Compare All", href: "/funding/compare" },
      { label: "Apply", href: "/funding/apply" },
    ],
  },
  {
    title: "Franchise",
    links: [
      { label: "Franchise Program", href: "/franchise" },
      { label: "Starter — $2,400 + $291/mo", href: "/franchise/checkout?tier=starter" },
      { label: "Grow-Rich — $9,999 + $991/mo", href: "/franchise/checkout?tier=grow_rich" },
      { label: "Enterprise — $24,000 + $1,991/mo", href: "/franchise/checkout?tier=enterprise" },
      { label: "Compare Tiers", href: "/franchise/compare" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Vlog", href: "/vlog" },
      { label: "Contact", href: "/contact" },
      { label: "Request Consultation", href: "/lead" },
      { label: "Partners & Press", href: "/partners" },
      { label: "Brand Kit · /logo.html", href: "/logo.html" },
      { label: "Manufacturer Download · Gated", href: "/brand-kit-access" },
      { label: "License the Brand — $4,500 / yr", href: "/license" },
      { label: "Embed Kit", href: "/embed" },
      { label: "Affiliate Program", href: "/affiliate" },
      { label: "System Status", href: "/status" },
      { label: "Install App", href: "/install" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms of Service", href: "/legal/terms" },
      { label: "Privacy Policy", href: "/legal/privacy" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", {
        email,
        segment: "general",
        tier: "public",
      });
      toast.success("Welcome to The Dispatch.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="footer" className="mt-32 pt-16 pb-10 px-6 lg:px-10 relative overflow-hidden" style={{ borderTop: "1px solid var(--hairline)" }}>
      {/* Large embossed monogram watermark */}
      <LogoMark
        height={420}
        width={420}
        ariaLabel=""
        testId="footer-logo-watermark"
        className="hidden md:block absolute pointer-events-none select-none"
        style={{ right: "-80px", bottom: "-60px", opacity: 0.05 }}
      />

      <div className="max-w-[1400px] mx-auto relative">
        {/* Brand + Newsletter row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <LogoMark height={140} width={140} testId="footer-logo" className="mb-6" />
            <p className="text-sm leading-relaxed max-w-2xl" style={{ color: "var(--ink-muted)" }}>
              We here at <span style={{ color: "var(--ink)" }}>WFI</span> consulting and marketing agency report on and
              archive the lives of successful stewards controlling Family-Office Trust Companies prospecting,
              CEOs for Fortune 100, founders of the houses for luxury goods and services. Quiet magnates who
              shape and influence the luxurious modern affluent world. Their quiet lessons by private invitation.
              Privilege insights by subscription tiers. Come learn and watch how the accredited quantum performers
              achieved <em style={{ color: "var(--brass)", fontFamily: "Cormorant Garamond" }}>Wealth First</em> —
              their grow-rich wisdom on the record, archived in long-form TV series, and hour-long interviews you can
              only watch here on <span style={{ color: "var(--brass)", letterSpacing: "0.05em" }}>WEALTH FIRST INC. TV</span>.
            </p>
          </div>

          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">The Dispatch</p>
            <p className="text-sm mb-5" style={{ color: "var(--ink-muted)" }}>
              A monthly letter. Wealth-building, preservation, and stewardship — reserved for the curious.
            </p>
            <form onSubmit={submit} className="flex gap-3 mb-8" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input-line flex-1"
                data-testid="footer-newsletter-email"
              />
              <button type="submit" disabled={busy} className="btn-brass !py-2 !px-4" data-testid="footer-newsletter-submit">
                {busy ? "..." : "Subscribe"}
              </button>
            </form>

            <p className="eyebrow mb-3">Follow</p>
            <div className="flex gap-4">
              <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="p-3 transition-colors hover:bg-[var(--surface)]" style={{ border: "1px solid var(--hairline)", color: "var(--brass)" }} data-testid="footer-youtube"><Youtube size={18} /></a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="p-3 transition-colors hover:bg-[var(--surface)]" style={{ border: "1px solid var(--hairline)", color: "var(--brass)" }} data-testid="footer-instagram"><Instagram size={18} /></a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="p-3 transition-colors hover:bg-[var(--surface)]" style={{ border: "1px solid var(--hairline)", color: "var(--brass)" }} data-testid="footer-facebook"><Facebook size={18} /></a>
              <a href={SOCIAL_LINKS.linktree} target="_blank" rel="noreferrer" aria-label="Linktree" className="p-3 transition-colors hover:bg-[var(--surface)]" style={{ border: "1px solid var(--hairline)", color: "var(--brass)" }} data-testid="footer-linktree"><LinkIcon size={18} /></a>
            </div>
          </div>
        </div>

        <hr className="hairline mb-12" />

        {/* Link groups */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {FOOTER_LINK_GROUPS.map((group) => (
            <div key={group.title}>
              <p className="eyebrow mb-5">{group.title}</p>
              <ul className="space-y-3">
                {group.links.map((l) => (
                  <li key={l.href}>
                    <a href={l.href} className="text-sm hover:text-[var(--brass)] transition-colors" style={{ color: "var(--ink-muted)" }}>
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <hr className="hairline mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <p className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--ink-faint)" }}>
            © {new Date().getFullYear()} Wealth First Inc. · All Rights Reserved
          </p>
          <p className="text-xs italic" style={{ color: "var(--ink-faint)", fontFamily: "Cormorant Garamond" }}>
            "Quietness is the chief sign of true wealth."
          </p>
        </div>
      </div>
    </footer>
  );
}
